import PropTypes from 'prop-types'
import React from 'react'
import { getData as apiGetData } from 'api/Api'
import { CancelError } from '@kanbo/api'
import { injectIntl, FormattedMessage } from 'react-intl'
import { PermissionLevels } from 'utils/permission'
import {
  ControlLabel,
  Button,
  ButtonGroup,
  FormControl,
  FormGroup
} from 'react-bootstrap'
import KanBoIcon from 'components/KanBoIcon'
import {
  ListItem
} from 'components/List'
import KanBoPicker, { styles } from 'components/KanBoPicker'
import CreatableSelect, { components } from 'react-select'
import { isCardLocation } from 'utils/url'
import classes from './CardPicker.module.scss'
import CardTemplateField from 'components/CardTemplateField'

const SelectOption = components.Option

export const Modes = {
  SEARCH: 'SEARCH',
  CREATE: 'CREATE',
  SELECT: 'SELECT'
}

const LIMIT = 20

const emptyArray = []

const CardSource = ({ boardName, listName }) =>
  (boardName && listName)
    ? (
      <div
        style={{
          fontSize: '85%',
          color: '#bbb'
        }}
      >
        {boardName}
        {' / '}
        {listName}
      </div>
    ) : null

const ComponentWithCardSource = ({ component: Component, children, ...props }) =>
  <Component {...props}>
    {children}
    {!props.data.__isNew__ &&
      <CardSource
        boardName={props.data.boardName}
        listName={props.data.listName}
      />
    }
  </Component>

const OptionWithCardSource = (props) => <ComponentWithCardSource {...props} component={SelectOption} />

export class CardPicker extends React.Component {
  static propTypes = {
    currentBoardId: PropTypes.number,
    currentStatusId: PropTypes.number,
    requiredPermissionLevel: PropTypes.number,
    isTemplate: PropTypes.bool,
    cardFilter: PropTypes.func,
    onModeChange: PropTypes.func,
    onBoardChange: PropTypes.func,
    onStatusChange: PropTypes.func,
    onCardChange: PropTypes.func,
    onTemplateChange: PropTypes.func,

    intl: PropTypes.object.isRequired,

    boardName: PropTypes.string.isRequired
  }

  static defaultProps = {
    requiredPermissionLevel: PermissionLevels.VISITOR
  }

  constructor (props) {
    super(props)

    this.state = {
      mode: null,
      cardInputValue: '',
      cardOption: null,
      cardOptions: emptyArray,
      cardLoading: false,
      boardId: props.currentBoardId ? props.currentBoardId.toString() : null,
      statusId: props.currentStatusId ? props.currentStatusId.toString() : null,
      template: null,
      limitToCurrentBoard: !!props.currentBoardId
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const {
      onModeChange,
      onBoardChange,
      onStatusChange,
      onCardChange,
      onTemplateChange,
    } = this.props
    const {
      mode,
      cardInputValue,
      cardOption,
      boardId,
      statusId,
      template,
    } = this.state

    if (mode !== prevState.mode) {
      onModeChange && onModeChange(mode)
    }

    if (cardInputValue !== prevState.cardInputValue) {
      this.fetchCards()
    }

    if (boardId !== prevState.boardId) {
      onBoardChange && onBoardChange(
        typeof boardId === 'string' ? +boardId : boardId
      )
    }

    if (statusId !== prevState.statusId) {
      onStatusChange && onStatusChange(
        typeof statusId === 'string' ? +statusId : statusId
      )
    }

    if (cardOption !== prevState.cardOption) {
      onCardChange && onCardChange(
        !cardOption ? null : cardOption.__isNew__ ? null : +cardOption.value,
        !cardOption ? null : cardOption.__isNew__ ? cardOption.value : null
      )
    }

    if (cardOption && cardOption.__isNew__ && template !== prevState.template) {
      onTemplateChange && onTemplateChange(template)
    }
  }

  componentWillUnmount () {
    this.cancelFetchCards()
  }

  fetchCards = () => {
    const {
      currentBoardId,
      requiredPermissionLevel,
      isTemplate,
      cardFilter
    } = this.props
    const {
      cardInputValue,
      limitToCurrentBoard
    } = this.state

    this.cancelFetchCards()

    if (!cardInputValue) {
      this.setState({
        cardOptions: emptyArray,
        cardLoading: false
      })
      return
    }

    this.setState({
      cardOptions: emptyArray,
      cardLoading: true
    })

    const cardId = isCardLocation(cardInputValue)

    if (cardId) {
      this.fetchCardsPromise = apiGetData('CardFront', {
        id: cardId
      })
      this.fetchCardsPromise.then(Card => {
        if (
          typeof isTemplate !== 'undefined' &&
          Card.IsTemplate !== isTemplate
        ) {
          this.setState({
            cardOptions: emptyArray,
            cardLoading: false
          })

          return
        }

        this.fetchCardsPromise = apiGetData('PermissionsForBoard', {
          boardId: Card.BoardId
        })
        this.fetchCardsPromise.then(Permissions => {
          if (Permissions.PermissionLevel >= requiredPermissionLevel) {
            let searchCardsOptions = [{
              value: Card.Id.toString(),
              label: Card.Name,
              id: Card.Id,
              boardName: Card.BoardName,
              listName: Card.ListName
            }]

            if (cardFilter) {
              searchCardsOptions = searchCardsOptions.filter(cardFilter)
            }

            this.setState({
              cardOption: searchCardsOptions[0],
              cardOptions: searchCardsOptions,
              cardLoading: false
            })
          } else {
            this.setState({
              cardOptions: emptyArray,
              cardLoading: false
            })
          }
        }).catch(() => {
          this.setState({
            cardOptions: emptyArray,
            cardLoading: false
          })
        })
      }).catch(() => {
        this.setState({
          cardOptions: emptyArray,
          cardLoading: false
        })
      })
    } else {
      this.fetchCardsPromise = apiGetData('SearchCards', {
        limit: LIMIT,
        query: cardInputValue,
        boardIds: limitToCurrentBoard ? [currentBoardId] : undefined,
        requiredPermissionLevel,
        isTemplate,
        isArchived: false
      })
      this.fetchCardsPromise.then(({ Results }) => {
        let searchCardsOptions =
          Results.map(Result => ({
            value: Result.Card.Id.toString(),
            label: Result.Card.Name,
            id: Result.Card.Id,
            boardName: Result.BoardName,
            listName: Result.ListName
          }))

        if (cardFilter) {
          searchCardsOptions = searchCardsOptions.filter(cardFilter)
        }

        this.setState({
          cardOptions: searchCardsOptions,
          cardLoading: false
        })
      }).catch((error) => {
        if (!(error instanceof CancelError)) {
          throw error
        }
      })
    }
  }

  cancelFetchCards = () => {
    if (this.fetchCardsPromise) {
      if (this.fetchCardsPromise.cancel) {
        this.fetchCardsPromise.cancel()
      }

      delete this.fetchCardsPromise
    }
  }

  handleSelectClick = () => {
    const {
      currentBoardId,
      currentStatusId
    } = this.props

    this.setState({
      mode: Modes.SELECT,
      cardOption: null,
      boardId: currentBoardId ? currentBoardId.toString() : null,
      statusId: currentStatusId ? currentStatusId.toString() : null
    })
  }

  handleCreateClick = () => {
    const {
      currentBoardId,
      currentStatusId
    } = this.props

    this.setState({
      mode: Modes.CREATE,
      cardOption: {
        __isNew__: true,
        value: '',
        label: ''
      },
      boardId: currentBoardId ? currentBoardId.toString() : null,
      statusId: currentStatusId ? currentStatusId.toString() : null
    })
  }

  handleCardSearchChange = (cardOption) => {
    this.setState({
      cardOption
    })
  }

  handleCardSearchInputChange = (cardInputValue) => {
    this.setState({
      cardInputValue
    })
  }

  handleBoardLimitClick = () => {
    this.setState({
      limitToCurrentBoard: !this.state.limitToCurrentBoard
    })
  }

  handleCardNameChange = (event) => {
    this.setState({
      cardOption: {
        __isNew__: true,
        value: event.target.value,
        label: event.target.value
      }
    })
  }

  handleBoardChange = (boardId) => {
    this.setState({
      boardId
    })
  }

  handleStatusChange = (statusId) => {
    this.setState({
      statusId
    })
  }

  handleCardChange = (cardId) => {
    this.setState({
      cardOption: {
        value: cardId,
        label: cardId
      }
    })
  }

  handleTemplateChange = (template) => {
    this.setState({
      template,
    })
  }

  render () {
    const {
      intl,

      currentBoardId,
      currentStatusId,
      requiredPermissionLevel,
      cardFilter,

      boardName
    } = this.props
    const {
      mode,
      boardId,
      template,
      cardOption,
      cardOptions,
      cardLoading,
      limitToCurrentBoard
    } = this.state

    return (
      <React.Fragment>
        {(!mode || mode === Modes.SEARCH) && (
          <div style={{ marginBottom: 10 }}>
            <ControlLabel>
              <FormattedMessage id='KANBO_PICKER_CARD_TITLE' />
            </ControlLabel>
            <CreatableSelect
              autoFocus
              allowCreateWhileLoading
              styles={styles}
              menuPlacement='auto'
              menuPosition='absolute'
              menuPortalTarget={document.body}
              value={cardOption}
              options={cardOptions}
              placeholder={intl.formatMessage({ id: 'KANBO_PICKER_CARD_SEARCH' })}
              loadingMessage={() => intl.formatMessage({ id: 'LOADING' })}
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? intl.formatMessage({ id: 'KANBO_PICKER_NO_RESULTS' })
                  : intl.formatMessage({ id: 'KANBO_PICKER_CARD_SEARCH' })
              }
              components={{
                Option: OptionWithCardSource,
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null
              }}
              isValidNewOption={(inputValue) => !!inputValue}
              formatCreateLabel={
                (inputValue) => intl.formatMessage(
                  { id: 'KANBO_PICKER_SELECT_CREATE' },
                  { cardName: inputValue }
                )
              }
              filterOption={() => true} // Filter is happening after api request
              isLoading={cardLoading}
              onChange={this.handleCardSearchChange}
              onInputChange={this.handleCardSearchInputChange}
            />
            {currentBoardId &&
              <ListItem
                icon={
                  <KanBoIcon
                    fixedWidth
                    name={limitToCurrentBoard ? 'kanbo-16-solid-check-box-checked' : 'kanbo-16-check-box-unchecked'}
                  />
                }
                className={classes.boardLimit}
                onClick={this.handleBoardLimitClick}
              >
                <FormattedMessage
                  id='SEARCH_BOARD_LIMIT'
                  values={{
                    boardName: <b>{boardName}</b>
                  }}
                />
              </ListItem>
            }
            {!mode &&
              <React.Fragment>
                <div className={classes.divider}>
                  <FormattedMessage id='OR' />
                </div>
                <ButtonGroup vertical block>
                  <Button
                    style={{ textAlign: 'left' }}
                    onClick={this.handleSelectClick}
                  >
                    <FormattedMessage id='KANBO_PICKER_CARD_SELECT' />
                  </Button>
                  <Button
                    style={{ textAlign: 'left' }}
                    onClick={this.handleCreateClick}
                  >
                    <FormattedMessage id='KANBO_PICKER_CARD_CREATE' />
                  </Button>
                </ButtonGroup>
              </React.Fragment>
            }
          </div>
        )}
        {mode === Modes.CREATE && (
          <React.Fragment>
            <FormGroup>
              <ControlLabel>
                <FormattedMessage id='KANBO_PICKER_CARD_NAME' />
              </ControlLabel>
              <FormControl
                autoFocus
                type='text'
                value={cardOption.value}
                onChange={this.handleCardNameChange}
              />
            </FormGroup>
            <KanBoPicker
              board
              status
              currentBoardId={currentBoardId}
              currentStatusId={currentStatusId}
              requiredPermissionLevel={requiredPermissionLevel}
              boardTitle={intl.formatMessage({ id: 'KANBO_PICKER_TARGET_BOARD' })}
              statusTitle={intl.formatMessage({ id: 'KANBO_PICKER_TARGET_STATUS' })}
              onBoardChange={this.handleBoardChange}
              onStatusChange={this.handleStatusChange}
            />
            {boardId &&
              <CardTemplateField
                boardId={Number(boardId)}
                value={template}
                onChange={this.handleTemplateChange}
              />
            }
          </React.Fragment>
        )}
        {mode === Modes.SELECT && (
          <KanBoPicker
            board
            status
            card
            cardFilter={cardFilter}
            currentBoardId={currentBoardId}
            currentStatusId={currentStatusId}
            requiredPermissionLevel={requiredPermissionLevel}
            onBoardChange={this.handleBoardChange}
            onStatusChange={this.handleStatusChange}
            onCardChange={this.handleCardChange}
          />
        )}
      </React.Fragment>
    )
  }
}

export default injectIntl(CardPicker)
